<script>
import { Plugins } from '@capacitor/core'

const { Geolocation, Device } = Plugins

export default {
  name: 'GoogleAddressAutocomplete',
  props: {
    value: { type: Object, required: true },
    label: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    prependInnerIcon: { type: String, default: '' },
    required: { type: Boolean, default: false },
    prefill: { type: Boolean, default: false }
  },
  data() {
    return {
      options: {
        componentRestrictions: { country: 'my' }
      },

      autocomplete: null,
      geocoder: null,
      places: null,
      predictions: [],

      search: '',
      fetching: false,
      dirty: false,
      platform: null,
      errors: []
    }
  },
  computed: {
    items() {
      if (!this.search) return []

      return this.predictions.reduce((acc, cur) => {
        if (!cur) return acc

        const { description, ...item } = cur
        acc.push({ name: description, ...item })

        return acc
      }, [])
    }
  },
  watch: {
    search(input) {
      const name = this.lodash.get(this, 'value.name', '')
      if (input && input !== name) {
        this.getPredictions(input)
      }
    },
    prefill(value) {
      if (value) this.prefillAddress()
    }
  },
  created() {
    this.getPredictions = this.lodash.debounce(this._getPredictions, 300, {
      maxWait: 750
    })

    // Get device platform (web or mobile)
    Device.getInfo().then(({ platform }) => {
      this.platform = platform
    })
  },
  mounted: function() {
    this.setupServices()

    // Prefill input
    if (this.value) {
      this.search = this.value.name
      this.predictions = [this.value]
    }
  },
  methods: {
    setupServices() {
      try {
        this.geocoder = new google.maps.Geocoder()
        this.autocomplete = new google.maps.places.AutocompleteService()

        console.log('GMaps service instances created')
      } catch (error) {
        setTimeout(this.setupServices, 100)
      }
    },

    prefillAddress() {
      if (this.geocoder) {
        this.getCurrentLocation().catch(error => {
          console.log(error)
          this.errors = [error.message]
          this.fetching = false
        })
      } else setTimeout(this.prefillAddress, 100)
    },

    showAll() {
      return true
    },

    getAutocompletePredictions(input) {
      if (!this.autocomplete) throw 'GMaps AutocompleteService is not available'
      const request = { ...this.options, input }

      return new Promise((resolve, reject) => {
        this.autocomplete.getPlacePredictions(request, (items, status) => {
          if (status != 'ZERO_RESULTS' && status != 'OK') {
            reject(status)
          }

          resolve(items || [])
        })
      })
    },

    getGeocodePredictions(input) {
      if (!this.geocoder) throw 'GMaps Geocoder is not available'

      return new Promise((resolve, reject) => {
        const request = { address: input, region: 'MY' }
        this.geocoder.geocode(request, (items, status) => {
          if (status != 'ZERO_RESULTS' && status != 'OK') {
            reject(status)
          }

          resolve(
            items.map(x => ({
              description: x.formatted_address,
              place_id: x.place_id
            }))
          )
        })
      })
    },

    _getPredictions(input) {
      if (!input) return
      this.errors = []
      this.fetching = true

      Promise.all([
        this.getAutocompletePredictions(input),
        this.getGeocodePredictions(input)
      ])
        .then(items => {
          this.predictions = this.lodash.flatten(items)
        })
        .catch(err => {
          console.error(err)
          this.predictions = []
        })
        .finally(() => {
          this.fetching = false
        })
    },

    async getCurrentPosition() {
      if (this.platform === 'web' && 'geolocation' in navigator) {
        // Using Web Geolocation API
        return new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject)
        })
      } else {
        // Using Capacitor
        return await Geolocation.getCurrentPosition()
      }
    },

    async getCurrentLocation() {
      this.fetching = true
      const { coords } = await this.getCurrentPosition()
      const { latitude: lat, longitude: lng } = coords

      this.geocoder.geocode({ location: { lng, lat } }, (results, status) => {
        if (status === 'OK' && results.length > 0) {
          const [place] = results
          const { formatted_address, place_id } = place

          const location = {
            name: formatted_address,
            place_id,
            latitude: lng,
            longitude: lat,
            ...this.getAddress(place)
          }

          this.$emit('input', location)
          this.predictions = [location]
          this.search = formatted_address
        }
        this.fetching = false
      })
    },

    onChange(input) {
      if (!input) return this.$emit('input', null)
      this.dirty = true

      const { name, place_id } = input

      this.geocoder.geocode({ placeId: place_id }, (results, status) => {
        if (status === 'OK' && results.length > 0) {
          const [place] = results
          const { lng, lat } = place.geometry.location

          this.$emit('input', {
            name,
            place_id,
            latitude: lng(),
            longitude: lat(),
            ...this.getAddress(place)
          })
        }
      })
    },

    getAddress(place) {
      const { place_id, address_components } = place

      const components = address_components.reduce((acc, cur) => {
        const [type] = cur.types
        acc[type] = cur.short_name

        return acc
      }, {})
      const { street_number = '', route, country, postal_code } = components

      return {
        place_id,
        street: [street_number, route].join(' ').trim(),
        city: components.locality,
        postal_code,
        country
      }
    },

    onBlur() {
      if (this.value && this.search !== this.value.name) {
        this.search = this.value.name
        this.$emit('input', { name: '', place_id: '' })
      }

      this.$emit('blur')
    }
  }
}
</script>

<template>
  <v-autocomplete
    :value="value"
    :loading="fetching"
    :rules="
      required
        ? [() => (!!value && !!value.name && !!value.place_id) || 'Required']
        : []
    "
    :error-messages="errors"
    validate-on-blur
    :search-input.sync="search"
    item-text="name"
    item-value="place_id"
    no-filter
    :label="label"
    :placeholder="placeholder"
    :items="items"
    auto-select-first
    hide-no-data
    hide-selected
    return-object
    eager
    class="address-autocomplete"
    :prepend-inner-icon="prependInnerIcon"
    @input="onChange"
    @blur="onBlur"
  />
</template>

<style lang="sass">
.address-autocomplete
  .v-input__append-inner
    display: none
  .v-select__selections
    flex-wrap: nowrap
    input
      width: 1px
  .v-select__selection
    max-width: 100%
    width: 100%
</style>
